import _extends from "@babel/runtime/helpers/extends";
import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { fullWidthMixin, heightMixin, widthMixin } from '../../mixins/layout';
import { BUTTON_CIRCLE_SIZE_TINY, BUTTON_HANDLE_OUTER_SIZE } from '../../Button/constants';
import { SLIDER_PROGRESS_BAR_HEIGHT, SLIDER_RAIL_CLICK_SIZE, SLIDER_RAIL_HORIZONTAL_SPACING, SLIDER_RAIL_TOUCH_SIZE, SLIDER_TICK_LABEL_TOP_POSITION } from './constants';
import { getPercent, getPx } from '../../helpers/common';
import { CircleIconButton } from '../../Button';
import { useSliderHeight } from './hooks/useSliderHeight';
import { SliderTickDot, SliderTickLabel } from './components';
import ProgressBar from '../../ProgressBar';
import { indexToOffset, indexToPercent, offsetToIndex, percentToIndex, valueToIndex } from './helpers';
import { ADVENTURE_GREEN, ELEMENT_GRAY } from '../../colors';
import { useDeviceType, useDidUpdateEffect, useInput, useMeasure, useSwipe, useTap } from '../../hooks';
import { HOOKS_TAP_TOLERANCE } from '../../constants';
import { isDefined } from '../../helpers/defined';
import { userSelectNoneStyle } from '../../tokens/user-select';
import { userDragNoneStyle } from '../../tokens/user-drag';
import { pointerEventsNoneStyle } from '../../tokens/pointer-events';
import { baseHighlightStyle } from '../../tokens/highlight';
import { height } from '../../style/height';
import { paddingHorizontal } from '../../style/padding-horizontal';
import { marginHorizontalNegative } from '../../style/margin-horizontal-negative';
const Wrapper = styled.div`
  position: relative;
  box-sizing: content-box;
  ${fullWidthMixin}
  ${widthMixin}
  ${heightMixin}
  ${baseHighlightStyle}
  ${userSelectNoneStyle}
  ${userDragNoneStyle}
  padding-bottom: ${({
  $tickMaxHeight,
  $tickTopPosition,
  $sliderHeight
}) => getPx($tickMaxHeight + $tickTopPosition - $sliderHeight / 2)};
`;
const ContentWrapper = styled.div`
  position: relative;
  box-sizing: border-box;
  ${fullWidthMixin}
  ${widthMixin}
  ${heightMixin}
`;
const TapWrapper = styled.div`
  position: absolute;
  box-sizing: content-box;
  width: 100%;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(calc(-50% + 6px));
  padding-top: 12px;
  padding-bottom: 24px;
  z-index: 3;
  ${({
  $hasTouch
}) => height($hasTouch ? SLIDER_RAIL_TOUCH_SIZE : SLIDER_RAIL_CLICK_SIZE)};
  cursor: ${({
  $disabled
}) => $disabled ? 'default' : 'pointer'};
  ${marginHorizontalNegative(SLIDER_RAIL_HORIZONTAL_SPACING)}
  ${paddingHorizontal(SLIDER_RAIL_HORIZONTAL_SPACING)}
`;
const TicksWrapper = styled.div`
  position: absolute;
  height: 0;
  width: 100%;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  ${userSelectNoneStyle}
`;
const StyledProgressBar = styled(ProgressBar)`
  position: absolute;
  width: 100%;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  ${pointerEventsNoneStyle}
  ${userSelectNoneStyle}
`;
const StyledCircleIconButton = styled(CircleIconButton)`
  position: absolute;
  top: 50%;
  left: ${({
  $percent
}) => getPercent($percent)};
  transform: translate(-50%, -50%);
  z-index: 4;
`;
const SliderOptions = ({
  alignLabels = true,
  value: valueProp,
  defaultValue,
  onUpdate,
  onChange,
  fullWidth = true,
  width,
  height = BUTTON_CIRCLE_SIZE_TINY,
  options = [],
  disabled = false,
  handleProps = {},
  handleLabel = 'Handle',
  sliderProps = {},
  sliderLabel = 'Slider',
  ...props
}) => {
  const {
    hasTouchScreen
  } = useDeviceType();
  const [value, setValue] = useInput(valueProp, defaultValue, onChange);
  const {
    tickMaxHeight,
    tickTopPosition,
    sliderHeight,
    measureTickHeight
  } = useSliderHeight({
    sliderHeight: height,
    tickTopPosition: SLIDER_TICK_LABEL_TOP_POSITION,
    options
  });
  const {
    setRef: setMeasureRef,
    bounds
  } = useMeasure();
  const containerWidth = bounds.width;
  const tapWrapperRef = useRef(null);
  const onTap = (e, {
    x
  }) => {
    const {
      left: wrapperX
    } = tapWrapperRef.current.getBoundingClientRect();
    const relativeX = x - (wrapperX + SLIDER_RAIL_HORIZONTAL_SPACING);
    if (relativeX <= 0) {
      setValue(options[0].value);
    } else if (relativeX >= containerWidth) {
      setValue(options[options.length - 1].value);
    } else {
      const percent = relativeX / containerWidth * 100;
      const index = percentToIndex(percent, options);
      setValue(options[index].value);
    }
  };
  const {
    setRef: setTapWrapperRef
  } = useTap(onTap, tapWrapperRef, hasTouchScreen, disabled);
  const [valueSwipe, setValueSwipe] = useState(null);
  const [offsetSwipe, setOffsetSwipe] = useState(null);
  const onSwipeStart = (e, {
    x
  }) => {
    const {
      left: wrapperX
    } = tapWrapperRef.current.getBoundingClientRect();
    const relativeX = x - (wrapperX + SLIDER_RAIL_HORIZONTAL_SPACING);
    const offset = Math.min(Math.max(relativeX, 0), containerWidth);
    const newIndex = offsetToIndex(offset, options, containerWidth);
    const newValue = options[newIndex].value;
    setValueSwipe(newValue);
    setOffsetSwipe(offset);
  };
  const onSwipeEnd = (e, {
    x
  }) => {
    if (containerWidth === 0 || Math.abs(x) < HOOKS_TAP_TOLERANCE) {
      if (valueSwipe !== value) {
        setValue(valueSwipe);
      }
      setValueSwipe(null);
      setOffsetSwipe(null);
      return;
    }
    const newIndex = offsetToIndex(offsetSwipe - x, options, containerWidth);
    const newValue = options[newIndex].value;
    setValue(newValue);
    setValueSwipe(null);
    setOffsetSwipe(null);
  };
  const {
    setRef: setSwipeHandleRef,
    offsetPosition: offsetHandlePosition
  } = useSwipe(onSwipeStart, onSwipeEnd, null, hasTouchScreen, disabled);
  const {
    setRef: setSwipeWrapperRef,
    offsetPosition: offsetWrapperPosition
  } = useSwipe(onSwipeStart, onSwipeEnd, null, hasTouchScreen, disabled);
  const offsetPositionX = valueSwipe === null || offsetSwipe === null ? 0 : offsetHandlePosition.x || offsetWrapperPosition.x || 0;
  const indexHandle = useMemo(() => {
    if (containerWidth === 0 || Math.abs(offsetPositionX) < HOOKS_TAP_TOLERANCE) {
      return valueToIndex(valueSwipe ?? value, options);
    }
    const offsetCurrent = offsetSwipe ?? indexToOffset(valueToIndex(valueSwipe ?? value, options), options, containerWidth);
    return offsetToIndex(offsetCurrent - offsetPositionX, options, containerWidth);
  }, [options, offsetPositionX, containerWidth, value, valueSwipe, offsetSwipe]);
  const percentHandle = useMemo(() => indexToPercent(indexHandle, options), [indexHandle, options]);
  const optionsCount = useMemo(() => options.filter(o => isDefined(o.label)).length, [options]);
  useDidUpdateEffect(() => {
    onUpdate?.(options[indexHandle].value);
  }, [indexHandle, options]);
  return /*#__PURE__*/React.createElement(Wrapper, _extends({
    $fullWidth: fullWidth,
    $width: width,
    $height: height,
    $tickTopPosition: tickTopPosition,
    $tickMaxHeight: tickMaxHeight,
    $sliderHeight: sliderHeight
  }, props), /*#__PURE__*/React.createElement(ContentWrapper, {
    ref: setMeasureRef,
    $fullWidth: fullWidth,
    $width: width,
    $height: height
  }, /*#__PURE__*/React.createElement(StyledProgressBar, _extends({
    animate: false,
    progressBarColor: disabled ? ELEMENT_GRAY : ADVENTURE_GREEN,
    height: SLIDER_PROGRESS_BAR_HEIGHT,
    progress: percentHandle,
    role: "slider",
    tabindex: "0",
    "aria-valuemin": 0,
    "aria-valuenow": indexHandle,
    "aria-valuemax": options.length - 1,
    "aria-valuetext": options[indexHandle].label,
    "aria-label": sliderLabel
  }, sliderProps)), /*#__PURE__*/React.createElement(TapWrapper, {
    ref: ref => {
      setTapWrapperRef(ref);
      setSwipeWrapperRef(ref);
    },
    $disabled: disabled,
    $hasTouch: hasTouchScreen
  }), /*#__PURE__*/React.createElement(TicksWrapper, null, options.map((option, idx) => isDefined(option.label) ? /*#__PURE__*/React.createElement(SliderTickDot, {
    key: idx,
    size: SLIDER_PROGRESS_BAR_HEIGHT,
    tick: {
      percent: idx / (options.length - 1) * 100,
      value: option.value,
      id: idx
    }
  }) : null), options.map((option, idx) => isDefined(option.label) ? /*#__PURE__*/React.createElement(SliderTickLabel, {
    key: idx,
    tick: {
      percent: idx / (options.length - 1) * 100,
      value: option.value,
      id: idx
    },
    count: optionsCount,
    label: option.label,
    measureHeight: measureTickHeight,
    onClick: setValue,
    align: alignLabels,
    disabled: disabled
  }) : null)), /*#__PURE__*/React.createElement(StyledCircleIconButton, _extends({
    ref: setSwipeHandleRef,
    $percent: percentHandle,
    sizeVariant: 'tiny',
    noIcon: true,
    noTransform: true,
    disabled: disabled,
    outerSize: BUTTON_HANDLE_OUTER_SIZE,
    "aria-label": handleLabel
  }, handleProps))));
};
export default SliderOptions;